import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  SelectChangeEvent,
  Skeleton
} from '@mui/material';
import { useBuilds } from '../context';

interface BuildsDropdownProps {
  /** Optional label for the dropdown */
  label?: string;
  /** Size variant for the dropdown */
  size?: 'small' | 'medium';
  /** Whether to show the label */
  showLabel?: boolean;
  /** Custom styling */
  sx?: object;
}

export const BuildsDropdown: React.FC<BuildsDropdownProps> = ({
  label = 'Select Build',
  size = 'small',
  showLabel = false,
  sx = {}
}) => {
  const { builds, selectedBuild, setSelectedBuild, isLoading } = useBuilds();

  const handleChange = (event: SelectChangeEvent<string>) => {
    const buildId = event.target.value;
    const build = builds.find(b => b.buildId.toString() === buildId) || null;
    setSelectedBuild(build);
  };

  // Don't render if no builds available (not loading and empty)
  if (!isLoading && builds.length === 0) {
    return null;
  }

  // Don't render if only one build (no need for dropdown)
  if (!isLoading && builds.length === 1) {
    return null;
  }

  return (
    <Box sx={{ minWidth: 200, ...sx }}>
      {showLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      <FormControl size={size} fullWidth>
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            height={size === 'small' ? 32 : 40}
            sx={{ borderRadius: 1 }}
          />
        ) : (
          <Select
            value={selectedBuild?.buildId?.toString() || ''}
            onChange={handleChange}
            displayEmpty
            aria-label={label}
            sx={{
              '& .MuiSelect-select': {
                py: 1,
                px: 2
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.dark',
                borderWidth: 1
              }
            }}
          >
            {builds.map(build => (
              <MenuItem key={build.buildId} value={build.buildId.toString()} sx={{ py: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {build.address}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>
    </Box>
  );
};
